/**
 * PhotoAttach — Review Shield v1.2 photo attachment control.
 *
 * Transient supporting evidence for the manager notification email
 * only. Selected files are held in component state ONLY — never sent
 * to the backend until the parent form is submitted. The parent then
 * POSTs to `/api/review-shield/{cardId}/feedback-with-photos` as
 * multipart/form-data. No URLs, no DB rows, no operator portal
 * display per spec.
 *
 * Native file input (NO `capture` attribute) so every browser shows its
 * full picker/chooser rather than being force-launched into a single camera
 * surface:
 *   - iPhone Safari: Photo Library + Take Photo or Video + Choose File
 *   - Android Chrome: Camera + Gallery/Photos + Files (chooser)
 *   - Desktop browsers: File picker
 *
 * IMPORTANT (mobile Chrome): do NOT add `capture` here. `capture="environment"`
 * makes Android Chrome bypass the chooser and open the rear camera activity
 * directly, which intermittently renders a blank/white capture surface (and
 * conflicts with `multiple`). The input is also hidden with `sr-only` (kept in
 * the render tree) rather than `display:none`, because some Android Chrome
 * builds ignore a programmatic `.click()` on a `display:none` file input.
 *
 * Spec: up to 3 photos, 10 MB max each, JPG/JPEG/PNG/HEIC accepted.
 */
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Camera, X, ImagePlus } from 'lucide-react';
import { toast } from 'sonner';

const MAX_PHOTOS = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT_MIME = 'image/jpeg,image/png,image/heic,image/heif,image/*';

const isAcceptableType = (file) => {
    const name = (file.name || '').toLowerCase();
    if (name.endsWith('.heic') || name.endsWith('.heif')) return true;
    return /^image\/(jpeg|jpg|png|heic|heif)$/i.test(file.type)
        // Some Android browsers omit the MIME on HEIC — accept by extension fallback above.
        || (/^image\//.test(file.type) && /\.(jpe?g|png|heic|heif)$/i.test(name));
};

const PhotoAttach = ({ photos, onChange, helperText, noticeText }) => {
    const { t } = useTranslation();
    const inputRef = useRef(null);

    const remaining = MAX_PHOTOS - photos.length;

    const handlePick = (e) => {
        const files = Array.from(e.target.files || []);
        // Reset the input so picking the same filename twice in a row works.
        if (inputRef.current) inputRef.current.value = '';
        if (!files.length) return;

        const accepted = [];
        for (const file of files) {
            if (photos.length + accepted.length >= MAX_PHOTOS) {
                toast.error(t('photoAttach.maxReached', { defaultValue: 'Maximum {{n}} photos', n: MAX_PHOTOS }));
                break;
            }
            if (!isAcceptableType(file)) {
                toast.error(t('photoAttach.invalidType', { defaultValue: '{{name}} — unsupported format', name: file.name }));
                continue;
            }
            if (file.size > MAX_BYTES) {
                toast.error(t('photoAttach.tooLarge', { defaultValue: '{{name}} — over 10 MB', name: file.name }));
                continue;
            }
            // Create a transient object-URL for thumbnail preview only. We
            // revoke it on removal / unmount to avoid leaks (handled by the
            // parent through the photos state — the URL stays referenced
            // while the file is in the array).
            accepted.push({
                file,
                previewUrl: URL.createObjectURL(file),
                id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
            });
        }
        if (accepted.length) onChange([...photos, ...accepted]);
    };

    const handleRemove = (id) => {
        const next = photos.filter((p) => {
            if (p.id === id) {
                try { URL.revokeObjectURL(p.previewUrl); } catch (_e) { /* noop */ }
                return false;
            }
            return true;
        });
        onChange(next);
    };

    return (
        <div className="space-y-2" data-testid="photo-attach" data-insight-id="review-shield-photo-attach">
            <div className="flex items-center gap-2">
                <Camera className="w-4 h-4 text-emerald-400" />
                <h3 className="text-sm font-semibold text-white">
                    {t('photoAttach.heading', 'Add Photos (Optional)')}
                </h3>
                <span className="text-xs text-slate-500 ml-auto" data-testid="photo-attach-count">
                    {photos.length}/{MAX_PHOTOS}
                </span>
            </div>
            <p className="text-xs text-slate-400 leading-relaxed">
                {helperText || t('photoAttach.helper',
                    'Attach a picture to help us better understand your experience. Examples: missing items, incorrect orders, presentation concerns, food quality, packaging.')}
            </p>

            {/* Advisory only — it never blocks, filters or rejects an upload. */}
            {noticeText && (
                <p className="text-xs text-amber-300/90 leading-relaxed" data-testid="photo-attach-notice">
                    {noticeText}
                </p>
            )}

            {photos.length > 0 && (
                <div className="grid grid-cols-3 gap-2" data-testid="photo-attach-previews">
                    {photos.map((p) => (
                        <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-white/5 border border-white/10">
                            <img
                                src={p.previewUrl}
                                alt={p.file.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                            />
                            <button
                                type="button"
                                onClick={() => handleRemove(p.id)}
                                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-black/90 text-white flex items-center justify-center transition-colors"
                                aria-label={t('photoAttach.removeAria', 'Remove photo')}
                                data-testid={`photo-attach-remove-${p.id}`}
                            >
                                <X className="w-3.5 h-3.5" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {remaining > 0 && (
                <button
                    type="button"
                    onClick={() => inputRef.current?.click()}
                    className="w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 border border-dashed border-white/15 text-white text-sm font-medium transition-colors flex items-center justify-center gap-2"
                    data-testid="photo-attach-trigger"
                >
                    <ImagePlus className="w-4 h-4" />
                    {photos.length === 0
                        ? t('photoAttach.addFirst', 'Add Photo')
                        : t('photoAttach.addMore', 'Add another')}
                </button>
            )}

            <input
                ref={inputRef}
                type="file"
                accept={ACCEPT_MIME}
                multiple
                onChange={handlePick}
                className="sr-only"
                aria-hidden="true"
                tabIndex={-1}
                data-testid="photo-attach-input"
            />
        </div>
    );
};

export default PhotoAttach;
export { MAX_PHOTOS, MAX_BYTES };
